import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BACKEND_BASE_URL } from "../config/env";
import { ONGKIR_COURIERS } from "../constants/ongkirCouriers";
import { COURIER_LABELS } from "../constants/couriers";
import Seo from "../components/Seo";
import Breadcrumb from "../components/Breadcrumb";
import AdBanner from "../components/AdBanner";
import { getBannerConfig } from "../config/bannerConfig";
import LazyAltcha from "../components/LazyAltcha";
import LazyIssoThread from "../components/LazyIssoThread";
import useTrackingOfferwall from "../components/useTrackingOfferwall";
import { normalizeCourierDisplay } from "../utils/courierDisplay";
import { trackFormSubmit } from "../utils/umami";
import "./styles/blog.css";

const RECENT_ONGKIR_CACHE_TTL = 24 * 60 * 60 * 1000;
const RECENT_ONGKIR_CACHE_KEY = "recent-ongkir-homepage";

export default function HomePage() {
  const navigate = useNavigate();
  const [resi, setResi] = useState("");
  const [courier, setCourier] = useState("jne");
  const [jneRecipientNumber, setJneRecipientNumber] = useState("");
  const [sayaPenerima, setSayaPenerima] = useState(false);
  const [, setCaptchaToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    resi: "",
    captcha: ""
  });
  const [recentOngkir, setRecentOngkir] = useState([]);
  const [recentOngkirLoading, setRecentOngkirLoading] = useState(false);
  const [recentOngkirError, setRecentOngkirError] = useState(null);
  const [latestBlogPosts, setLatestBlogPosts] = useState([]);
  const [blogHelpers, setBlogHelpers] = useState(null);
  const recentOngkirRef = useRef(null);
  const blogSectionRef = useRef(null);
  const [hasTriggeredHistoryFetch, setHasTriggeredHistoryFetch] = useState(false);
  const [, setCaptchaRequested] = useState(false);
  const altchaRef = useRef(null);
  const { requestOfferwall, offerwallElement } = useTrackingOfferwall();
  const courierOptions = useMemo(() => {
    const entries = Object.entries(COURIER_LABELS);
    const collator = new Intl.Collator("id", { sensitivity: "base" });
    entries.sort((a, b) => collator.compare(a[1], b[1]));
    return entries;
  }, []);

  useEffect(() => {
    const node = blogSectionRef.current;
    let cancelled = false;
    let observer;
    let timeoutId;

    const loadBlogPreview = () => {
      import("../constants/blogPosts").then((mod) => {
        if (cancelled) {
          return;
        }

        setLatestBlogPosts(mod.SORTED_BLOG_POSTS.slice(0, 3));
        setBlogHelpers({
          formatBlogDate: mod.formatBlogDate,
          getBlogCardImageSrc: mod.getBlogCardImageSrc,
        });
      });
    };

    if (node && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            loadBlogPreview();
            observer.disconnect();
          }
        },
        { rootMargin: "300px" }
      );
      observer.observe(node);
    } else {
      timeoutId = window.setTimeout(loadBlogPreview, 2500);
    }

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!BACKEND_BASE_URL || hasTriggeredHistoryFetch) return;

    const node = recentOngkirRef.current;
    let observer;
    let timeoutId;

    const trigger = () => setHasTriggeredHistoryFetch(true);

    if (node && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              trigger();
              observer.disconnect();
            }
          });
        },
        { rootMargin: "200px" }
      );
      observer.observe(node);
    } else {
      timeoutId = window.setTimeout(trigger, 1500);
    }

    return () => {
      if (observer) observer.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [BACKEND_BASE_URL, hasTriggeredHistoryFetch]);

  useEffect(() => {
    if (!BACKEND_BASE_URL || !hasTriggeredHistoryFetch) return;

    let cancelled = false;

    const fetchRecentOngkir = async () => {
      try {
        const cachedRaw = localStorage.getItem(RECENT_ONGKIR_CACHE_KEY);
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw);
          const isFresh =
            cached &&
            typeof cached.timestamp === "number" &&
            Date.now() - cached.timestamp < RECENT_ONGKIR_CACHE_TTL;

          if (!cancelled && isFresh && Array.isArray(cached.items)) {
            setRecentOngkir(cached.items);
            setRecentOngkirError(null);
            setRecentOngkirLoading(false);
            return;
          }

          localStorage.removeItem(RECENT_ONGKIR_CACHE_KEY);
        }
      } catch {
        localStorage.removeItem(RECENT_ONGKIR_CACHE_KEY);
      }

      try {
        setRecentOngkirLoading(true);
        setRecentOngkirError(null);

        const url = `${BACKEND_BASE_URL}/ongkir/history?offset=0&limit=5`;
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`RESPON_TIDAK_OK_${res.status}`);
        }

        const json = await res.json();
        if (!cancelled && Array.isArray(json.items)) {
          setRecentOngkir(json.items);
          localStorage.setItem(
            RECENT_ONGKIR_CACHE_KEY,
            JSON.stringify({
              timestamp: Date.now(),
              items: json.items,
            })
          );
        }
      } catch (err) {
        if (!cancelled) {
          setRecentOngkirError("Gagal memuat riwayat cek ongkir.");
        }
      } finally {
        if (!cancelled) {
          setRecentOngkirLoading(false);
        }
      }
    };

    fetchRecentOngkir();

    return () => {
      cancelled = true;
    };
  }, [BACKEND_BASE_URL, hasTriggeredHistoryFetch]);

  const formatHistoryTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Baru saja";
    if (diffMins < 60) return `${diffMins} menit lalu`;
    if (diffHours < 24) return `${diffHours} jam lalu`;
    if (diffDays < 7) return `${diffDays} hari lalu`;

    return date.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleRecentOngkirClick = (entry) => {
    const createSlug = (text) => {
      return text
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^\w-]/g, "")
        .replace(/-+/g, "-");
    };

    const originSlug = createSlug(entry.origin);
    const destSlug = createSlug(entry.destination);

    navigate(
      `/ongkir/${entry.origin_id}-${originSlug}/${entry.destination_id}-${destSlug}/${entry.weight}/${entry.couriers}`
    );
  };

  useEffect(() => {
    if (courier !== "jne" && jneRecipientNumber) {
      setJneRecipientNumber("");
    }
  }, [courier, jneRecipientNumber]);

  useEffect(() => {
    resetCaptchaToken();
  }, [courier]);

  const resetCaptchaToken = () => {
    altchaRef.current?.reset?.();
    setCaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const nomorResi = resi.trim();
      const errors = { resi: "", captcha: "" };
      let hasError = false;

      if (!nomorResi) {
        errors.resi = "Nomor resi wajib diisi.";
        hasError = true;
      }

      let activeToken = "";
      if (altchaRef.current?.getToken) {
        activeToken = await altchaRef.current.getToken();
      }

      if (!activeToken) {
        errors.captcha = "Mohon tunggu/selesaikan verifikasi keamanan (CAPTCHA).";
        hasError = true;
      }

      setFieldErrors(errors);
      if (hasError) {
        setFormError(errors.captcha || "Mohon lengkapi semua field dengan benar.");
        return;
      }

      setLoading(true);
      setFormError("");
      trackFormSubmit("resi-tracking", {
        source: "home",
        courier,
        jne_number_provided: courier === "jne" && Boolean(jneRecipientNumber),
      });

      try {
        sessionStorage.setItem(
          `pending_force_${courier}_${nomorResi}`,
          JSON.stringify({
            force: "1",
            altcha_token: activeToken || "",
          })
        );
      } catch (e) {
        console.error("Failed to set sessionStorage force token", e);
      }

      const params = new URLSearchParams();
      if (courier === "jne" && jneRecipientNumber) {
        params.append("number", jneRecipientNumber);
      }
      if (sayaPenerima) {
        params.append("saya_penerima", "1");
      }

      const queryStr = params.toString();
      const redirectUrl = `/kurir/${encodeURIComponent(courier)}/${encodeURIComponent(nomorResi)}${queryStr ? `?${queryStr}` : ""}`;
      await requestOfferwall();

      // Redirect browser to the detail page (triggering a full page reload)
      window.location.href = redirectUrl;
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLoadingState = () => (
    <div className="skeleton-card" aria-hidden="true">
      <div className="skeleton-line wide" />
      <div className="skeleton-line medium" />
      <div className="skeleton-line short" />
      <div className="skeleton-line wide" />
      <div className="skeleton-line medium" />
    </div>
  );

  const renderHistorySkeleton = () => (
    <div className="history-list" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, idx) => (
        <div className="history-item history-item-loading" key={idx}>
          <div className="history-item-main">
            <div className="history-route">
              <span className="skeleton-block wide" />
              <span className="skeleton-block medium" />
            </div>
            <div className="history-details">
              <span className="skeleton-pill" />
              <span className="skeleton-dot" />
              <span className="skeleton-block short" />
            </div>
          </div>
          <div className="history-item-time">
            <span className="skeleton-block short" />
          </div>
        </div>
      ))}
    </div>
  );

  const showRecentOngkirSkeleton =
    Boolean(BACKEND_BASE_URL) && (!hasTriggeredHistoryFetch || recentOngkirLoading);

  return (
    <>
      {offerwallElement}
      <Seo
        title="Cek Resi & Cek Ongkir Semua Ekspedisi - cek-resi.net"
        description="Cek resi dan cek ongkir dari berbagai ekspedisi Indonesia seperti JNE, JNT, SiCepat, POS, TIKI dan lainnya dalam satu halaman."
        breadcrumb={[{ name: "Beranda", path: "/" }]}
      />
      <Breadcrumb items={[{ name: "Beranda", path: "/" }]} />
      <main className="main-content">
        <section className="hero hero-home">
        <div className="hero-inner hero-grid">
          <div className="hero-left">
            <Link to="/" className="brand-minimal brand-logo">
              <img
                src="/logo.png"
                alt="Logo cek-resi.net"
                width="40"
                height="40"
              />
              <span>cek-resi.net</span>
            </Link>
            <h1 className="hero-title">
              <span>Cek Resi Pengiriman</span>
            </h1>
            <p className="hero-subtitle">
              Lacak status paket dari berbagai ekspedisi dengan satu halaman
              sederhana.
            </p>
            <p className="hero-tagline">Bebas iklan yang mengganggu</p>
            <ul className="hero-points">
              <li>• Masukkan nomor resi dan pilih kurir</li>
              <li>• Lihat ringkasan pengiriman dan riwayat tracking</li>
              <li>• Gratis digunakan, tanpa perlu login</li>
            </ul>
            <div className="hero-disclaimer" role="note" aria-label="Disclaimer situs">
              <strong>Disclaimer:</strong> cek-resi.net adalah situs independen untuk
              pelacakan resi dan informasi pengiriman. Kami tidak terafiliasi,
              tidak mewakili, dan tidak bertindak atas nama pihak, perusahaan,
              ekspedisi, marketplace, atau institusi mana pun.
              Situs ini tidak meminta transfer, OTP, PIN, password, atau biaya
              penanganan paket.
            </div>
          </div>

          <div className="hero-right">
            <div className="home-card">
              <form
                onSubmit={handleSubmit}
                onFocus={() => setCaptchaRequested(true)}
                onPointerDown={() => setCaptchaRequested(true)}
                className="tracking-form tracking-form-stack"
              >
                <div className="stack-field">
                  <label className="field-label" htmlFor="resi-input">
                    Nomor Resi
                  </label>
                  <input
                    id="resi-input"
                    type="text"
                    value={resi}
                    onChange={(e) => {
                      setResi(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, resi: "" }));
                      setFormError("");
                    }}
                    placeholder="Masukkan nomor resi (contoh: JP1234567890)"
                    className={`input ${fieldErrors.resi ? "input-error" : ""}`}
                    aria-invalid={fieldErrors.resi ? "true" : undefined}
                    aria-describedby={fieldErrors.resi ? "resi-input-error" : undefined}
                  />
                  {fieldErrors.resi && (
                    <p className="form-error" id="resi-input-error" role="alert">
                      {fieldErrors.resi}
                    </p>
                  )}
                </div>
                <div className="stack-field">
                  <label className="field-label" htmlFor="courier-select">
                    Kurir
                  </label>
                  <select
                    id="courier-select"
                    value={courier}
                    onChange={(e) => {
                      setCourier(e.target.value);
                      if (e.target.value !== "jne") {
                        setJneRecipientNumber("");
                      }
                    }}
                    className="select"
                  >
                    {courierOptions.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  className={`stack-field conditional-field-slot ${courier === "jne" ? "" : "is-hidden"}`}
                  aria-hidden={courier === "jne" ? undefined : "true"}
                >
                  <div className="conditional-field-content">
                    <label className="field-label" htmlFor="jne-recipient">
                      Nomor Penerima JNE (Opsional)
                    </label>
                    <input
                      id="jne-recipient"
                      type="text"
                      value={jneRecipientNumber}
                      onChange={(e) => setJneRecipientNumber(e.target.value)}
                      placeholder="5 digit nomor penerima (contoh: 12345)"
                      maxLength="5"
                      className="input"
                    />
                    <p className="helper-text" style={{ marginTop: "4px", fontSize: "0.85rem" }}>
                      Nomor ini membantu identifikasi tambahan pada tracking JNE
                    </p>
                  </div>
                </div>
                <LazyAltcha
                  ref={altchaRef}
                  onVerify={(token) => {
                    setCaptchaToken(token);
                    setFieldErrors((prev) => ({ ...prev, captcha: "" }));
                    setFormError("");
                  }}
                />
              <div style={{ display: "none" }} aria-hidden="true">
                <label htmlFor="saya_penerima_check_home">Saya Penerima</label>
                <input
                  id="saya_penerima_check_home"
                  type="checkbox"
                  name="saya_penerima"
                  tabIndex="-1"
                  autoComplete="off"
                  checked={sayaPenerima}
                  onChange={(e) => setSayaPenerima(e.target.checked)}
                />
              </div>
              <button type="submit" className="button-primary button-wide" disabled={loading}>
                {loading ? "Melacak..." : "Lacak Paket"}
              </button>
              {formError && (
                <p className="form-error" role="alert">
                  {formError}
                </p>
              )}
            </form>

            <p className="helper-text" aria-live="polite">
              Kami tidak menyimpan nomor resi Anda. Data hanya digunakan
              untuk menampilkan status pengiriman.
            </p>
            </div>
          </div>
        </div>
      </section>

      {loading && (
        <section className="result-section" aria-live="polite">
          {renderLoadingState()}
        </section>
      )}

        <AdBanner {...getBannerConfig("trackingResultBanner")} />

        <section className="courier-detail-section">
          <div className="result-section">
            <div className="ongkir-section-header">
              <h2 className="ongkir-section-title">
                Cek Ongkir Kurir
              </h2>
              <div className="ongkir-section-actions">
                <Link to="/ongkir" className="button-primary ongkir-section-button">
                  Cek Ongkir Semua Kurir
                </Link>
                <Link to="/ongkir/history" className="button-secondary ongkir-section-button">
                  Lihat Riwayat Lengkap
                </Link>
              </div>
            </div>
            <div className="courier-cards-grid">
              {ONGKIR_COURIERS.map((courier) => (
                <Link
                  key={courier.code}
                  to={`/ongkir/${courier.code}`}
                  className="courier-card-link"
                  style={{textDecoration: 'none'}}
                >
                  <div className="courier-card">
                    <div className="courier-card-name">{courier.name}</div>
                    <div className="courier-card-type">{courier.type}</div>
                    <div className="courier-card-arrow">→</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {BACKEND_BASE_URL && (
          <section className="result-section" ref={recentOngkirRef}>
            <h2>5 Cek Ongkir Terakhir</h2>
            <p className="helper-text">
              Riwayat ringkas cek ongkir publik dalam 365 hari terakhir.
            </p>
            {showRecentOngkirSkeleton && renderHistorySkeleton()}
            {!showRecentOngkirSkeleton && recentOngkirError && (
              <p className="error" role="alert">
                {recentOngkirError}
              </p>
            )}
            {!showRecentOngkirSkeleton && !recentOngkirError && recentOngkir.length === 0 && (
              <p className="placeholder-text">
                Belum ada riwayat cek ongkir yang bisa ditampilkan.
              </p>
            )}
            {!showRecentOngkirSkeleton && !recentOngkirError && recentOngkir.length > 0 && (
              <div className="history-list">
                {recentOngkir.map((item, idx) => (
                  <div
                    key={idx}
                    className="history-item"
                    onClick={() => handleRecentOngkirClick(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleRecentOngkirClick(item);
                      }
                    }}
                  >
                    <div className="history-item-main">
                      <div className="history-route">
                        <span className="location">{item.origin}</span>
                        <span className="arrow">→</span>
                        <span className="location">{item.destination}</span>
                      </div>
                      <div className="history-details">
                        <span className="weight">{item.weight}g</span>
                        <span className="separator">•</span>
                        <span className="couriers">
                          {normalizeCourierDisplay(item.couriers)}
                        </span>
                      </div>
                    </div>
                    <div className="history-item-time">
                      {formatHistoryTime(item.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <section className="result-section" ref={blogSectionRef}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
            <div>
              <h2 style={{ marginBottom: "8px" }}>3 Post Terbaru</h2>
              <p className="helper-text" style={{ margin: 0 }}>
                Update terbaru seputar pengiriman, operasional kurir, dan tips praktis.
              </p>
            </div>
            <Link to="/blog" className="button-secondary" style={{ textDecoration: "none", whiteSpace: "nowrap" }}>
              Lihat Semua Post
            </Link>
          </div>

          <div className="blog-list home-blog-list">
            {blogHelpers && latestBlogPosts.map((post) => (
              <article key={post.slug} className="blog-card home-blog-card">
                <Link to={`/blog/${post.slug}`} className="blog-card-link">
                  {post.image && (
                    <div className="blog-card-image">
                      <img
                        src={blogHelpers.getBlogCardImageSrc(post.image)}
                        alt={post.imageAlt || post.title}
                        width="800"
                        height="400"
                        loading="lazy"
                        decoding="async"
                        sizes="(max-width: 640px) calc(100vw - 16px), (max-width: 960px) calc((100vw - 72px) / 2), 293px"
                      />
                    </div>
                  )}
                  <div className="blog-card-body">
                    <h3 className="blog-card-title">{post.title}</h3>
                    <p className="blog-card-desc">{post.description}</p>
                    <div className="blog-card-meta">
                      <time dateTime={post.date}>{blogHelpers.formatBlogDate(post.date)}</time>
                      <span className="blog-card-author">{post.author}</span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="result-section">
          <h2>Diskusi &amp; Pertanyaan</h2>
          <p className="helper-text">
            Sampaikan masukan atau pertanyaan seputar cek resi dan cek ongkir di
            bawah ini.
          </p>
          <LazyIssoThread identifier="/homepage" />
        </section>
      </main>
    </>
  );
}
